import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/articles';

const contactFormUrl = 'https://readdy.ai/api/form/d9u53ptca23r0tr1aqqg';

export default function AboutPage() {
  useEffect(() => {
    document.title = `关于我们 | ${siteInfo.name}`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) metaDescription.setAttribute('content', `${siteInfo.name} - ${siteInfo.description.slice(0, 120)}`);
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${siteInfo.name}`,
    description: siteInfo.aboutIntro,
    about: {
      '@type': 'Organization',
      name: siteInfo.name,
      description: siteInfo.description,
      foundingDate: siteInfo.foundingYear,
      email: siteInfo.email,
      telephone: siteInfo.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: siteInfo.address,
        addressLocality: '广州市',
        addressRegion: '广东省',
        addressCountry: 'CN',
      },
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <Breadcrumb items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]} />
              <div className="pb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">关于我们</h1>
                <p className="text-sm text-foreground-500">了解{siteInfo.name}的品牌故事与使命</p>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Brand Introduction */}
            <section className="py-10 md:py-14">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-1">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">品牌介绍</h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{siteInfo.aboutIntro}</p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    我们的团队由一群深耕行业多年的资深从业者组成，对注册送彩金市场有着深刻的理解和敏锐的洞察力。
                    我们坚持"真实、透明、专业"的核心价值观，通过严格的信息筛选和验证机制，
                    确保每一条发布在{siteInfo.name}上的资讯都经过反复核验，让玩家能够放心参考、安心选择。
                  </p>
                </div>
                <div className="w-full md:w-64 h-48 md:h-56 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20team%20working%20in%20modern%20minimalist%20office%20with%20warm%20golden%20lighting%2C%20collaborative%20atmosphere%2C%20clean%20design%20aesthetic%2C%20cream%20and%20amber%20color%20palette%2C%20friendly%20professional%20vibe&width=512&height=512&seq=about-brand&orientation=squarish"
                    alt="彩金汇团队"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="py-10 md:py-14 border-t border-background-200/70">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">品牌信誉</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {siteInfo.reputation.map((item) => (
                  <div
                    key={item.label}
                    className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70 text-center"
                  >
                    <p className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">{item.value}</p>
                    <p className="text-xs md:text-sm text-foreground-500">{item.label}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 bg-background-50 rounded-lg p-6 border border-background-200/70">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">我们的承诺</h3>
                <ul className="space-y-2.5">
                  {[
                    '信息真实性：所有平台活动信息均经过人工核实，确保准确可靠',
                    '评测公正性：平台评测基于客观数据，不受商业合作影响',
                    '更新及时性：活动信息每日更新，第一时间推送最新福利',
                    '用户隐私保护：严格保护用户隐私信息，绝不泄露给第三方',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground-600">
                      <i className="ri-check-double-line text-primary-500 mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Contact */}
            <section className="py-10 md:py-14 border-t border-background-200/70">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">联系我们</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                        <i className="ri-mail-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">电子邮件</p>
                        <p className="text-sm text-foreground-500">{siteInfo.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                        <i className="ri-phone-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">客服热线</p>
                        <p className="text-sm text-foreground-500">{siteInfo.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">公司地址</p>
                        <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">给我们留言</h3>
                    <form
                      className="space-y-4"
                      data-readdy-form=""
                      onSubmit={async (e) => {
                        e.preventDefault();
                        const form = e.currentTarget;
                        const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                        if (honeypot) return;
                        if (!contactFormUrl) {
                          alert('表单功能即将上线，请通过邮箱联系我们');
                          return;
                        }
                        const formData = new FormData(form);
                        const params = new URLSearchParams();
                        for (const [k, v] of formData.entries()) {
                          if (k !== 'phone_alt' && typeof v === 'string') params.append(k, v);
                        }
                        try {
                          const res = await fetch(contactFormUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                            body: params.toString(),
                          });
                          const text = await res.text();
                          let parsed;
                          try { parsed = JSON.parse(text); } catch { parsed = null; }
                          if (res.ok && parsed?.code === 'OK') {
                            alert('留言已发送，我们会尽快回复您！');
                            form.reset();
                          } else {
                            const msg = parsed?.meta?.message || parsed?.message || '发送失败，请稍后重试';
                            alert(msg);
                          }
                        } catch {
                          alert('网络错误，请稍后重试');
                        }
                      }}
                    >
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        className="w-full py-2.5 bg-primary-500 text-background-50 font-medium text-sm rounded-md hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        发送留言
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}